<template>
  <!-- ProProxyUI: settings read from the box; a changed value is written to the box when the server is saved -->
  <div class="float-left w-full px-2 pb-2 modal-item">
    <div class="bx">
      <div class="bx-head">
        <b>Box settings</b>
        <span class="bx-state">{{ state }}</span>
        <button type="button" class="bx-btn" :disabled="loading" @click="read">{{ loading ? 'Reading…' : 'Read from box' }}</button>
      </div>
      <!-- the second (SOCKS5) listening port, read-only (set on the box): only when the box has one -->
      <div v-if="loaded && altOffset" class="bx-alt"><span class="bx-dim">Second port (SOCKS5)</span><b>{{ altText }}</b></div>
      <!-- this dashboard's choice, shown from the start; the box itself is never switched (other dashboards may share it) -->
      <div class="bx-mode">
        <label>How this dashboard's customers connect</label>
        <div class="bx-seg">
          <button type="button" :class="{ on: !vps }" @click="vps = false">NAT</button>
          <button type="button" :class="{ on: vps }" :disabled="loaded && !vpsInfo.on" @click="vps = true">VPS</button>
        </div>
        <small v-if="!loaded" class="bx-dim">Enter the domain and API token above: the box's settings for this mode appear here.</small>
        <small v-else-if="!vpsInfo.on" class="bx-dim">VPS mode is off on the box, so only NAT is available.</small>
        <p v-if="loaded && uuid && vps !== savedVps && soldPorts" class="bx-warn">{{ soldPorts }} sold port{{ soldPorts === 1 ? '' : 's' }} will get a new address and new VPN files when you save.</p>
      </div>
      <template v-if="loaded">
        <div v-if="!vps" class="bx-grid">
          <div class="bx-wide"><label>Box address (NAT)</label><input v-model="model.publicHost" type="text" class="form-control shadow-none rounded-0 text-black"></div>
          <div><label>Proxy ports from</label><input v-model.number="model.portRangeStart" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
          <div><label>Proxy ports to</label><input v-model.number="model.portRangeEnd" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
          <div><label>WireGuard port (shared)</label><input v-model.number="model.wgPublicPort" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
          <div><label>OpenVPN port (shared)</label><input v-model.number="model.ovpnPublicPort" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
          <div><label>OpenVPN protocol</label>
            <select v-model="model.ovpnProto" class="form-control shadow-none rounded-0 text-black"><option value="udp">UDP</option><option value="tcp">TCP</option></select></div>
        </div>
        <div v-else class="bx-grid">
          <div><label>VPS mode</label><div class="bx-ro">On · VPS IP {{ vpsInfo.ip || '—' }}</div></div>
          <div><label>Address customers use</label><div class="bx-ro">{{ vpsInfo.host || '—' }}</div></div>
          <div><label>Proxy ports from</label><input v-model.number="model.portRangeStart" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
          <div><label>Proxy ports to</label><input v-model.number="model.portRangeEnd" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
          <div><label>WireGuard port</label><div class="bx-ro">proxy port + {{ vpsInfo.wg_offset }}</div></div>
          <div><label>OpenVPN port</label><div class="bx-ro">proxy port + {{ vpsInfo.ovpn_offset }}</div></div>
          <div><label>OpenVPN protocol</label>
            <select v-model="model.ovpnProto" class="form-control shadow-none rounded-0 text-black"><option value="udp">UDP</option><option value="tcp">TCP</option></select></div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineModel, defineProps } from 'vue';
import { fetchWrapper } from '@/helpers';

const props = defineProps({ domain: String, username: String, password: String, uuid: String });
const model = defineModel({ type: Object, default: () => ({}) });
const vps = defineModel('vps', { type: Boolean, default: false });   // modems.using_vps: NAT (false) or VPS (true)
const vpsInfo = ref({ on: false });
const soldPorts = ref(0);
const savedVps = ref(false);
const API = `${process.env.VUE_APP_VUE_API_URL}`;
const loading = ref(false);
const loaded = ref(false);
const state = ref('Enter the box address and API token');

const altOffset = ref(0);   // the box's second port: HTTP + this (0 = one port for HTTP and SOCKS5)
const altText = computed(() => `HTTP ${altOffset.value > 0 ? '+' : '−'} ${Math.abs(altOffset.value)}${altOffset.value === -3000 ? ' · ProxySmart' : altOffset.value === 1000 ? ' · XProxy' : ''}`);
const read = async () => {
  if (!props.domain || (!props.password && !props.uuid)) { state.value = 'Enter the box address and API token'; return; }
  loading.value = true;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/read-settings`, { domain: props.domain, username: props.username || '', password: props.password || '', uuid: props.uuid || undefined });
    model.value = { ...r.settings };
    vpsInfo.value = r.vps || { on: false };
    soldPorts.value = Number(r.sold_ports) || 0;
    altOffset.value = Number(r.alt_offset) || 0;
    savedVps.value = r.using_vps === true;
    if (!vpsInfo.value.on) vps.value = false;   // VPS mode off on the box: NAT only
    loaded.value = true;
    state.value = r.box ? `v${r.box.version} · ${r.box.modems_online} of ${r.box.modems_total} modems online · ${r.box.ports} ports` : 'Read from the box';
  } catch (e) {
    state.value = typeof e === 'string' ? e : 'The box did not answer';
  }
  loading.value = false;
};
// read once the address and token are in (and again when either changes)
let timer = null;
watch(() => [props.domain, props.password, props.uuid], () => { clearTimeout(timer); timer = setTimeout(read, 700); }, { immediate: true });
</script>

<style scoped>
.bx { border: 1px solid var(--ui-line); border-radius: 10px; padding: 10px 12px; }
.bx-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.bx-state { color: var(--ui-dim); font-size: 12px; flex: 1; }
.bx-alt { margin-top: 10px; display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 6px 10px; border-radius: 8px; background: var(--ui-row-alt); font-size: 13px; }
.bx-alt b { font-weight: 500; }
.bx-btn { padding: 3px 10px; border: 1px solid var(--ui-line-strong); border-radius: 6px; font-size: 12px; background: transparent; color: inherit; cursor: pointer; }
.bx-btn:disabled { opacity: 0.5; cursor: default; }
.bx-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 12px; margin-top: 10px; }
.bx-wide { grid-column: 1 / -1; }
.bx-mode { margin-top: 10px; display: flex; flex-direction: column; gap: 4px; }
.bx-mode > label { color: var(--ui-dim); font-size: 12px; }
.bx-seg { display: inline-grid; grid-template-columns: 1fr 1fr; width: 220px; border: 1px solid var(--ui-line-strong); border-radius: 8px; overflow: hidden; }
.bx-seg button { padding: 5px 10px; font-size: 13px; background: transparent; color: inherit; }
.bx-seg button.on { background: var(--ui-pri); color: #ffffff; }
.bx-seg button:disabled { opacity: 0.45; cursor: not-allowed; }
.bx-dim { color: var(--ui-dim); font-size: 12px; }
.bx-warn { margin: 2px 0 0; padding: 6px 10px; border-radius: 8px; background: var(--ui-warn-soft); color: var(--ui-warn-ink); font-size: 12.5px; }
.bx-ro { padding: 6px 0; font-size: 13.5px; }
.bx-grid label { display: block; color: var(--ui-dim); font-size: 12px; margin-bottom: 2px; }
</style>
